
import { defineComponent, onMounted, ref, watchEffect } from "vue";
import { useStore } from "vuex";
import {
    GET_SHIPPING_ADDRESS_REPORT,
    SEARCH_SHIPPING_ADDRESS_REPORT,
    GET_SHIPPING_ADDRESS_GROUP_DETAIL
} from "../graphql/Queries";
import Table from "../../../../components/Table/Table.vue";
import InnerLoader from "../../../../components/InnerLoader.vue";
import { Apollo } from "@/core/services";
import AdvanceSearch from "../../../../components/search/AdvanceSearch.vue";
import { setPageHistory } from "@/core/helpers/toolbar";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

const MODE_ORDERS = "orders";
const MODE_BY_ADDRESS = "by_address";
const MODE_ADDRESS_GROUP_ORDERS = "address_group_orders";
const MODE_FLAT = "flat_line";

/** Page size for orders inside the address-group detail modal */
const DETAIL_PAGE_SIZE = 10;

export default defineComponent({
    components: {
        Table,
        InnerLoader,
        AdvanceSearch
    },
    setup() {
        const loader = ref(true);
        const store = useStore();
        const pagination = ref({}) as Record<string, any>;
        const rows: any = ref([]);
        const clickHandler = ref() as any;
        const currentPage = ref(0);
        const searchQueryActive = ref(false);
        const activeIndex = ref(-1 as any);
        const showDelete = ref(false);
        const filterOption = ref({}) as Record<string, any>;
        const router = useRouter();
        const systemLocale = ref();
        const i18n = useI18n();
        const columnName = ref(null) as any;
        const columnSort = ref(null) as any;
        const reportMode = ref(MODE_ORDERS);
        const reportModeOptions = ref([
            { value: MODE_ORDERS, labelKey: "message.CUSTOMER_REPORT_ORDERS_WITH_ADDRESS_LINES" },
            { value: MODE_BY_ADDRESS, labelKey: "message.CUSTOMER_REPORT_BY_DELIVERY_ADDRESS" },
            {
                value: MODE_ADDRESS_GROUP_ORDERS,
                labelKey: "message.CUSTOMER_REPORT_ADDRESS_GROUP_ORDER_DETAIL"
            },
            { value: MODE_FLAT, labelKey: "message.CUSTOMER_REPORT_FLAT_ORDER_LINES" }
        ]);

        const detailDialogVisible = ref(false);
        const detailDialogTitle = ref("");
        const detailLoading = ref(false);
        const detailOrders: any = ref([]);
        const detailPagination = ref({
            total: 0,
            current_page: 1,
            per_page: DETAIL_PAGE_SIZE,
            last_page: 1,
            has_more_pages: false,
            from: 0,
            to: 0
        } as Record<string, any>);
        const detailGroupContext = ref(null as null | { customerUuid: string; addressGroupKey: string });
        const detailEmbeddedAll: any = ref([]);

        const emptyDetailPagination = () => ({
            total: 0,
            current_page: 1,
            per_page: DETAIL_PAGE_SIZE,
            last_page: 1,
            has_more_pages: false,
            from: 0,
            to: 0
        });

        filterOption.value = {
            name: true,
            customer_report_email: true,
            customer_report_status: true
        };

        const columns = ref([] as any[]);
        const statusList = ref([
            { label: "message.ALL", value: "all" },
            { label: "message.ACTIVE", value: 1 },
            { label: "message.INACTIVE", value: 0 }
        ]);

        const setColumns = () => {
            const mode = reportMode.value;
            if (mode === MODE_BY_ADDRESS) {
                columns.value = [
                    { label: "message.NAME", key: "customer_name", sorting: true },
                    { label: "message.EMAIL", key: "email", sorting: true },
                    { label: "message.ADDRESS", key: "delivery_address" },
                    { label: "message.ORDERS", key: "order_count", sorting: true },
                    { label: "message.TOTAL", key: "address_total", sorting: true },
                    { label: "message.ACTIONS", key: "actions", textAlignment: "center" }
                ];
            } else if (mode === MODE_ADDRESS_GROUP_ORDERS) {
                columns.value = [
                    { label: "message.NAME", key: "customer_name", sorting: true },
                    { label: "message.EMAIL", key: "email", sorting: true },
                    { label: "message.ADDRESS", key: "delivery_address" },
                    { label: "message.ORDERS", key: "order_count", sorting: true },
                    { label: "message.TOTAL", key: "address_total", sorting: true },
                    { label: "message.DETAILS", key: "group_detail", textAlignment: "center" },
                    { label: "message.ACTIONS", key: "actions", textAlignment: "center" }
                ];
            } else if (mode === MODE_FLAT) {
                columns.value = [
                    { label: "message.NAME", key: "customer_name", sorting: true },
                    { label: "message.EMAIL", key: "email", sorting: true },
                    { label: "message.ORDER", key: "order" },
                    { label: "message.ADDRESS", key: "delivery_address" },
                    { label: "message.PRODUCT", key: "line_product" },
                    { label: "message.QUANTITY", key: "line_qty" },
                    { label: "message.TOTAL", key: "line_total" },
                    { label: "message.ACTIONS", key: "actions", textAlignment: "center" }
                ];
            } else {
                columns.value = [
                    { label: "message.NAME", key: "customer_name", sorting: true },
                    { label: "message.EMAIL", key: "email", sorting: true },
                    { label: "message.ORDER", key: "order" },
                    { label: "message.ORDER_DATE", key: "order_date", sorting: true },
                    { label: "message.ADDRESS", key: "delivery_address" },
                    { label: "message.TOTAL", key: "order_total", sorting: true },
                    { label: "message.PRODUCTS", key: "order_lines" },
                    { label: "message.ACTIONS", key: "actions", textAlignment: "center" }
                ];
            }
        };

        const formatAddress = (a: any) => {
            if (!a) return "—";
            const parts = [a.name, a.address_1, a.address_2, a.city, a.postal_code, a.country?.name].filter(Boolean);
            return parts.length ? parts.join(", ") : "—";
        };

        const orderIdLabel = (order: any) => {
            if (order == null || order.id === undefined || order.id === null || order.id === "") {
                return "";
            }
            return `${i18n.t("message.ORDER_ID")} #${order.id}`;
        };

        const keyForRow = (item: any) => {
            if (reportMode.value === MODE_FLAT) {
                return `${item?.order?.id ?? "o"}-${item?.line?.product_name ?? "p"}-${item?.line?.line_total ?? "t"}`;
            }
            if (reportMode.value === MODE_BY_ADDRESS || reportMode.value === MODE_ADDRESS_GROUP_ORDERS) {
                return `${item?.customer?.id ?? "c"}-${item?.address_group_key ?? ""}-${item?.address_total ?? "a"}-${formatAddress(item?.shipping_address)}`;
            }
            return item?.order?.id ?? String(Math.random());
        };

        const getReport = (page = 1, reload = false) => {
            loader.value = true;
            columnName.value = null;
            columnSort.value = null;
            const sub = Apollo.watchQuery({
                query: GET_SHIPPING_ADDRESS_REPORT,
                errorPolicy: "all",
                fetchPolicy: "network-only",
                nextFetchPolicy: "cache-only",
                variables: {
                    page: page,
                    limit: 10,
                    showDeleted: showDelete.value,
                    report_mode: reportMode.value,
                    col: columnName.value,
                    type: columnSort.value == true ? "asc" : "desc"
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                if (reload === true) {
                    currentPage.value = 1;
                }
                searchQueryActive.value = false;
                clickHandler.value = getReport;
                const payload = data?.shipping_address_report;
                rows.value = Array.isArray(payload?.data) ? payload.data : [];
                pagination.value = payload && typeof payload === "object" ? payload : {};
                loader.value = false;
                sub.unsubscribe();
            });
        };

        const searchHandler = (page = 1, reload = false, colName = null, type: any = null) => {
            if (colName != null) columnName.value = colName;
            if (type != null) columnSort.value = type;
            searchQueryActive.value = true;
            const searchData = store.getters.getSearchData;
            loader.value = true;
            const search = Apollo.watchQuery({
                query: SEARCH_SHIPPING_ADDRESS_REPORT,
                errorPolicy: "all",
                fetchPolicy: "network-only",
                nextFetchPolicy: "cache-only",
                variables: {
                    page: page,
                    limit: 10,
                    filter: JSON.stringify(searchData?.data?.select),
                    search_key: searchData?.data?.input,
                    locale: systemLocale.value,
                    showDeleted: showDelete.value,
                    col: columnName.value,
                    type: columnSort.value == true ? "asc" : "desc",
                    report_mode: reportMode.value
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                clickHandler.value = searchHandler;
                const payload = data?.search_shipping_address_report;
                rows.value = Array.isArray(payload?.data) ? payload.data : [];
                pagination.value = payload && typeof payload === "object" ? payload : {};
                loader.value = false;
                search.unsubscribe();
            });
        };

        const onReportModeChange = () => {
            setColumns();
            columnName.value = null;
            columnSort.value = null;
            if (searchQueryActive.value) {
                searchHandler(1, true, null, null);
            } else {
                getReport(1, true);
            }
        };

        const reloadReportListing = () => {
            showDelete.value = false;
            if (searchQueryActive.value) {
                searchHandler(1, true, null, null);
            } else {
                getReport();
            }
        };

        const changeShowDelete = () => {
            const pageHistory = store.getters.getPageHistory;
            if (searchQueryActive.value) {
                searchHandler(pageHistory.page_number, false, columnName.value, columnSort.value);
            } else {
                getReport(pageHistory.page_number, false);
            }
        };

        const handleViewCustomer = (row: any) => {
            const val = row?.customer;
            if (!val) return;
            const searchData = store.getters.getSearchData;
            setPageHistory({
                filter: searchData?.data?.select,
                search_key: searchData?.data?.input,
                page_number: pagination.value?.current_page
            });
            router.push({
                name: "customer_detail",
                params: { uuid: val.uuid, orderTab: "true" }
            });
        };

        const handleOrderView = (order: any) => {
            if (!order?.uuid) return;
            detailDialogVisible.value = false;
            const searchData = store.getters.getSearchData;
            setPageHistory({
                filter: searchData?.data?.select,
                search_key: searchData?.data?.input,
                page_number: pagination.value?.current_page
            });
            router.push({ name: "order_details", params: { uuid: order.uuid } });
        };

        const onDetailDialogClosed = () => {
            detailOrders.value = [];
            detailDialogTitle.value = "";
            detailEmbeddedAll.value = [];
            detailGroupContext.value = null;
            detailPagination.value = emptyDetailPagination();
        };

        const applyEmbeddedDetailSlice = (page: number) => {
            const all = detailEmbeddedAll.value;
            const per = DETAIL_PAGE_SIZE;
            const total = all.length;
            const lastPage = Math.max(1, Math.ceil(total / per));
            const p = Math.min(Math.max(1, page), lastPage);
            const start = (p - 1) * per;
            detailOrders.value = all.slice(start, start + per);
            detailPagination.value = {
                total,
                current_page: p,
                per_page: per,
                last_page: lastPage,
                has_more_pages: p < lastPage,
                from: total ? start + 1 : 0,
                to: Math.min(start + per, total)
            };
        };

        const loadDetailPage = async (page = 1) => {
            if (detailEmbeddedAll.value.length > 0) {
                applyEmbeddedDetailSlice(page);
                detailLoading.value = false;
                return;
            }
            const ctx = detailGroupContext.value;
            if (!ctx) {
                detailOrders.value = [];
                detailPagination.value = emptyDetailPagination();
                return;
            }
            detailLoading.value = true;
            try {
                const result = await Apollo.query({
                    query: GET_SHIPPING_ADDRESS_GROUP_DETAIL,
                    variables: {
                        customer_uuid: ctx.customerUuid,
                        address_group_key: ctx.addressGroupKey,
                        showDeleted: showDelete.value,
                        page,
                        limit: DETAIL_PAGE_SIZE
                    },
                    fetchPolicy: "network-only"
                });
                const payload = result?.data?.shipping_address_group_detail;
                const ob = payload?.orders;
                if (ob && Array.isArray(ob.data)) {
                    detailOrders.value = ob.data;
                    detailPagination.value = {
                        total: ob.total ?? 0,
                        current_page: ob.current_page ?? page,
                        per_page: ob.per_page ?? DETAIL_PAGE_SIZE,
                        last_page: ob.last_page ?? 1,
                        has_more_pages: !!ob.has_more_pages,
                        from: ob.from ?? 0,
                        to: ob.to ?? 0
                    };
                } else if (Array.isArray(ob)) {
                    detailEmbeddedAll.value = ob;
                    detailGroupContext.value = null;
                    applyEmbeddedDetailSlice(page);
                } else {
                    detailOrders.value = [];
                    detailPagination.value = emptyDetailPagination();
                }
            } finally {
                detailLoading.value = false;
            }
        };

        const onDetailOrdersPageChange = (p: number) => {
            loadDetailPage(p);
        };

        const openAddressGroupDetail = async (item: any) => {
            detailDialogTitle.value = formatAddress(item?.shipping_address);
            detailDialogVisible.value = true;
            detailEmbeddedAll.value = [];
            detailGroupContext.value = null;
            detailOrders.value = [];
            detailPagination.value = emptyDetailPagination();
            detailLoading.value = true;

            const embedded = item?.drilldown_orders;
            if (Array.isArray(embedded) && embedded.length > 0) {
                detailEmbeddedAll.value = embedded;
                await loadDetailPage(1);
                return;
            }
            const key = item?.address_group_key;
            const customerUuid = item?.customer?.uuid;
            if (!key || !customerUuid) {
                detailLoading.value = false;
                return;
            }
            detailGroupContext.value = { customerUuid, addressGroupKey: key };
            await loadDetailPage(1);
        };

        const handleCurrentChange = (page: number) => {
            if (clickHandler.value) {
                clickHandler.value(page, false, columnName.value, columnSort.value);
            }
        };

        const dropdownHandler = (index: any, id: any) => {
            activeIndex.value = index === id ? null : id;
        };

        const exportFile = (data: any) => {
            const searchData = store.getters.getSearchData;
            loader.value = true;
            const sub = Apollo.watchQuery({
                query: SEARCH_SHIPPING_ADDRESS_REPORT,
                errorPolicy: "all",
                fetchPolicy: "network-only",
                variables: {
                    page: 0,
                    limit: 1,
                    filter: searchData?.data?.select ? JSON.stringify(searchData.data.select) : "",
                    search_key: searchData?.data?.input ? searchData?.data?.input : "",
                    types: data?.type,
                    locale: systemLocale.value,
                    showDeleted: showDelete.value,
                    date_from: data?.dateFrom,
                    date_to: data?.dateTo,
                    report_mode: reportMode.value
                }
            }).subscribe((result: any) => {
                if (result?.errors) {
                    loader.value = false;
                } else if (result?.data) {
                    const row = result.data?.search_shipping_address_report?.data?.[0];
                    const path = row?.path ?? row?.customer?.path;
                    if (path) {
                        window.open(path, "_blank");
                    }
                }
                loader.value = false;
                sub.unsubscribe();
            });
        };

        watchEffect(() => {
            systemLocale.value = i18n.fallbackLocale.value;
        });

        onMounted(() => {
            setColumns();
            getReport();
        });

        return {
            rows,
            columns,
            pagination,
            filterOption,
            statusList,
            handleCurrentChange,
            searchHandler,
            getReport,
            reloadReportListing,
            changeShowDelete,
            exportFile,
            loader,
            showDelete,
            reportMode,
            reportModeOptions,
            onReportModeChange,
            formatAddress,
            orderIdLabel,
            handleViewCustomer,
            handleOrderView,
            dropdownHandler,
            activeIndex,
            keyForRow,
            detailDialogVisible,
            detailDialogTitle,
            detailLoading,
            detailOrders,
            detailPagination,
            openAddressGroupDetail,
            onDetailDialogClosed,
            onDetailOrdersPageChange
        };
    }
});
